/**
 * A single narrowband probe: how far is the signal from *this* frequency?
 *
 * The transform-and-search approach has to resolve a semitone at the low E,
 * and a transform only resolves 1/T — which is where the 683 ms window came
 * from. A probe that already knows which frequency it is looking for has a
 * different limit entirely: it only has to reject whatever else is nearby, and
 * in a guitar tuning the nearest competing partial sits 24 to 83 Hz away.
 *
 * How it works:
 *
 *  1. Mix the signal down by the probe frequency (multiply by a complex
 *     exponential). Whatever was at that frequency is now at DC; everything
 *     else is offset by however far away it was.
 *  2. Low-pass what is left. A cascade of one-poles: the order is what buys
 *     the selectivity, not the cutoff — two poles let a neighbouring string
 *     drag the reading by tens of cents, four keep it under a cent at the same
 *     speed.
 *  3. The phase of the baseband turns at the *difference* between the signal
 *     and the probe. Summing `z[n] · conj(z[n-1])` over a window and taking
 *     the argument gives that difference, weighted by amplitude, which is what
 *     makes it robust when the string is quiet.
 *
 * Everything is streaming and allocation-free: this runs on every sample of
 * every probe.
 */

import { CENTS_PER_OCTAVE } from '../music/pitch'

/** How a probe is set up. */
export interface ProbeOptions {
  /** Frequency the probe is centred on, in Hz. */
  readonly frequency: number
  /** Rate of the samples pushed in, in Hz. */
  readonly sampleRate: number
  /** Half-bandwidth of the low-pass, in Hz. */
  readonly bandwidthHz: number
  /** Length of the averaging window, in samples. */
  readonly windowSamples: number
  /** Low-pass order; four is the point where neighbours stop leaking. */
  readonly poles?: number
}

/** What a probe currently sees. */
export interface ProbeReading {
  /** Deviation from the probe frequency in cents, or null before it fills. */
  readonly cents: number | null
  /** Mean amplitude over the window, on the same scale as the input. */
  readonly amplitude: number
}

/** Low-pass order below which a neighbouring string leaks into the reading. */
export const DEFAULT_POLES = 4

/**
 * Samples between renormalisations of the mixing oscillator.
 *
 * The oscillator advances by repeated complex multiplication, which is far
 * cheaper than a sine per sample but drifts in magnitude over millions of
 * steps. Rescaling occasionally costs nothing and bounds the drift.
 */
const RENORMALISE_EVERY = 8192

/** One narrowband probe. */
export class Probe {
  readonly frequency: number
  private readonly sampleRate: number
  private readonly alpha: number

  /** Mixing oscillator: a unit complex number and its per-sample rotation. */
  private readonly stepRe: number
  private readonly stepIm: number
  private oscRe = 1
  private oscIm = 0
  private sinceRenormalise = 0

  /** Low-pass state, one complex accumulator per pole. */
  private readonly poleRe: Float64Array
  private readonly poleIm: Float64Array

  /** Previous baseband sample, for the phase difference. */
  private previousRe = 0
  private previousIm = 0

  /** Sliding window over the phase products and the magnitudes. */
  private readonly windowRe: Float64Array
  private readonly windowIm: Float64Array
  private readonly windowMag: Float64Array
  private cursor = 0
  private filled = false
  private sumRe = 0
  private sumIm = 0
  private sumMag = 0

  constructor(options: ProbeOptions) {
    const { frequency, sampleRate, bandwidthHz, windowSamples } = options
    if (!(frequency > 0) || !(sampleRate > 0)) {
      throw new RangeError('frequency and sampleRate must be positive')
    }
    if (!(bandwidthHz > 0)) throw new RangeError('bandwidthHz must be positive')
    if (!Number.isInteger(windowSamples) || windowSamples < 2) {
      throw new RangeError('windowSamples must be an integer of at least 2')
    }

    this.frequency = frequency
    this.sampleRate = sampleRate

    const radiansPerSample = (-2 * Math.PI * frequency) / sampleRate
    this.stepRe = Math.cos(radiansPerSample)
    this.stepIm = Math.sin(radiansPerSample)
    this.alpha = 1 - Math.exp((-2 * Math.PI * bandwidthHz) / sampleRate)

    const poles = options.poles ?? DEFAULT_POLES
    this.poleRe = new Float64Array(poles)
    this.poleIm = new Float64Array(poles)

    this.windowRe = new Float64Array(windowSamples)
    this.windowIm = new Float64Array(windowSamples)
    this.windowMag = new Float64Array(windowSamples)
  }

  /** Length of the averaging window in samples. */
  get windowSamples(): number {
    return this.windowRe.length
  }

  /** True once the window holds a full set of samples. */
  get isReady(): boolean {
    return this.filled
  }

  /** Feed one sample. */
  push(sample: number): void {
    let re = this.oscRe * this.stepRe - this.oscIm * this.stepIm
    let im = this.oscRe * this.stepIm + this.oscIm * this.stepRe
    this.oscRe = re
    this.oscIm = im

    this.sinceRenormalise += 1
    if (this.sinceRenormalise >= RENORMALISE_EVERY) {
      this.sinceRenormalise = 0
      const scale = 1 / Math.hypot(this.oscRe, this.oscIm)
      this.oscRe *= scale
      this.oscIm *= scale
    }

    re = sample * this.oscRe
    im = sample * this.oscIm
    for (let p = 0; p < this.poleRe.length; p += 1) {
      this.poleRe[p]! += this.alpha * (re - this.poleRe[p]!)
      this.poleIm[p]! += this.alpha * (im - this.poleIm[p]!)
      re = this.poleRe[p]!
      im = this.poleIm[p]!
    }

    // z[n] * conj(z[n-1]): its argument is the phase advanced in one sample,
    // and its magnitude weights the sum by how strong the tone was.
    const productRe = re * this.previousRe + im * this.previousIm
    const productIm = im * this.previousRe - re * this.previousIm
    const magnitude = Math.hypot(re, im)
    this.previousRe = re
    this.previousIm = im

    this.sumRe += productRe - this.windowRe[this.cursor]!
    this.sumIm += productIm - this.windowIm[this.cursor]!
    this.sumMag += magnitude - this.windowMag[this.cursor]!
    this.windowRe[this.cursor] = productRe
    this.windowIm[this.cursor] = productIm
    this.windowMag[this.cursor] = magnitude

    this.cursor += 1
    if (this.cursor === this.windowRe.length) {
      this.cursor = 0
      this.filled = true
    }
  }

  /** Feed a block of samples. */
  pushBlock(samples: Float32Array): void {
    for (const sample of samples) this.push(sample)
  }

  /** Read the current deviation and amplitude. Does not clear the window. */
  read(): ProbeReading {
    // The baseband is complex, so its magnitude is the amplitude of the
    // analytic signal — half of the real tone that produced it.
    const amplitude = (2 * this.sumMag) / this.windowMag.length
    if (!this.filled) return { cents: null, amplitude }

    const offsetHz = (Math.atan2(this.sumIm, this.sumRe) * this.sampleRate) / (2 * Math.PI)
    const measured = this.frequency + offsetHz
    if (measured <= 0) return { cents: null, amplitude }

    return { cents: CENTS_PER_OCTAVE * Math.log2(measured / this.frequency), amplitude }
  }

  /** Forget everything, e.g. after the input was switched. */
  reset(): void {
    this.oscRe = 1
    this.oscIm = 0
    this.sinceRenormalise = 0
    this.poleRe.fill(0)
    this.poleIm.fill(0)
    this.previousRe = 0
    this.previousIm = 0
    this.windowRe.fill(0)
    this.windowIm.fill(0)
    this.windowMag.fill(0)
    this.cursor = 0
    this.filled = false
    this.sumRe = 0
    this.sumIm = 0
    this.sumMag = 0
  }
}
